import { isEmail } from './email.validator';

/**
 * Which kinds of contact value the validator should accept.
 * - `email-only` – only an email address is valid.
 * - `phone-only` – only a phone number is valid.
 * - `either`     – an email address *or* a phone number is valid (email is tried first).
 */
export type ContactInfoMode = 'email-only' | 'phone-only' | 'either';

/**
 * What the value was recognised as. Mirrors the backend `ContactMethod`
 * (`Email`/`PhoneNumber`) on the `Invite` entity, plus `invalid` for input that
 * matched nothing usable.
 */
export type ContactKind = 'email' | 'phone' | 'invalid';

/**
 * Country support for a phone number.
 * - `supported`   – a valid US/CA (NANP, `+1`) number.
 * - `unsupported` – a well-formed number whose country is outside the allow
 *   list. Distinct from a malformed number so the UI can say *why* it was
 *   rejected.
 */
export type CountrySupport = 'supported' | 'unsupported';

export interface ContactInfoResult {
    /**
     * Whether the value is a valid, accepted contact for its `kind` — a valid
     * email, or a well-formed phone number in a supported (US/CA) country. A
     * well-formed number in an unsupported country is `false`; see `country` to
     * tell that apart from a malformed number.
     */
    isValid: boolean;
    kind: ContactKind;
    /**
     * Country support, present only when the value is a phone number well-formed
     * enough to determine its country: `supported` (US/CA) or `unsupported`.
     * Omitted for malformed numbers, emails, and unrecognised input — so a
     * missing `country` with `isValid: false` means "malformed", while
     * `country: 'unsupported'` means "valid number, country not supported".
     */
    country?: CountrySupport;
}

/** Characters that may legitimately appear in a typed phone number. */
const PHONE_CHARS = /^[+()\-.\s\d]+$/;

/**
 * A valid 10-digit NANP national number: area code + exchange both start 2-9,
 * followed by the 4-digit line number.
 */
const NANP_NATIONAL = /^[2-9]\d{2}[2-9]\d{6}$/;

/** Validate a 10-digit NANP national number (a supported US/CA number). */
const evaluateNanp = (national: string): ContactInfoResult =>
    NANP_NATIONAL.test(national)
        ? { isValid: true, kind: 'phone', country: 'supported' }
        : { isValid: false, kind: 'phone' };

/**
 * Evaluate a value as a phone number.
 * Returns `null` when the value is not phone-shaped at all (e.g. contains
 * letters), so callers can fall back to other validation.
 */
const evaluatePhone = (value: string): ContactInfoResult | null => {
    if (!value || !PHONE_CHARS.test(value)) {
        return null;
    }

    const digits = value.replace(/\D/g, '');

    if (digits.length === 0) {
        return null;
    }

    // E.164: a leading `+` followed by 8-15 digits with a non-zero country code.
    if (value.startsWith('+')) {
        if (digits.startsWith('0') || digits.length < 8 || digits.length > 15) {
            return { isValid: false, kind: 'phone' };
        }

        /*
         * Country code `1` is the North American Numbering Plan (US/CA); apply
         * NANP rules. Any other country is well-formed E.164 but outside the
         * allow list: not accepted (`isValid: false`), but flagged as a
         * recognised number whose country is simply not supported.
         */
        if (!digits.startsWith('1')) {
            return { isValid: false, kind: 'phone', country: 'unsupported' };
        }

        return evaluateNanp(digits.slice(1));
    }

    // Common US/CA local formats, optionally with a leading `1` country code.
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

    return evaluateNanp(national);
};

/**
 * Validate a contact value as an email address and/or a phone number.
 *
 * Phone numbers must be E.164 (`+1…`) or a common US/CA local format. A
 * well-formed number for a country outside the allow list returns
 * `{ isValid: false, kind: 'phone', country: 'unsupported' }` so the UI can
 * explain that the country is not supported rather than showing a generic
 * "invalid number" message.
 *
 * @param value Raw input value (whitespace is trimmed).
 * @param mode  Which kinds of value to accept. Defaults to `either`.
 */
export const validateContactInfo = (
    value: string,
    mode: ContactInfoMode = 'either'
): ContactInfoResult => {
    const trimmed = value?.trim() ?? '';

    if (mode === 'email-only') {
        return isEmail(trimmed)
            ? { isValid: true, kind: 'email' }
            : { isValid: false, kind: 'invalid' };
    }

    if (mode === 'phone-only') {
        return evaluatePhone(trimmed) ?? { isValid: false, kind: 'invalid' };
    }

    // 'either' – prefer email, then fall back to phone.
    if (isEmail(trimmed)) {
        return { isValid: true, kind: 'email' };
    }

    return evaluatePhone(trimmed) ?? { isValid: false, kind: 'invalid' };
};
